import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  signInWithPopup,
  signOut as fbSignOut,
  onIdTokenChanged,
  reauthenticateWithPopup,
  type User,
} from 'firebase/auth';
import { auth, googleProvider } from './firebase';

/** R-1.5 — idle session timeout. Mirrors DEFAULT_APP_CONFIG.sessionTimeoutMinutes. */
const IDLE_TIMEOUT_MS = 12 * 60 * 60 * 1000;
/** R-1.6 — a credential older than this is not fresh enough for a sensitive action. */
const REAUTH_WINDOW_MS = 5 * 60 * 1000;

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'visibilitychange'] as const;

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  signIn: () => Promise<void>;
  signOut: (reason?: string) => Promise<void>;
  /** Returns true if the current credential is fresh enough for a sensitive action. */
  isRecentlyAuthenticated: () => boolean;
  /** Forces a fresh Google sign-in. Call before any SENSITIVE_ACTIONS operation. R-1.6. */
  reauthenticate: () => Promise<boolean>;
  signedOutReason: string | null;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signedOutReason, setSignedOutReason] = useState<string | null>(null);
  const lastActivity = useRef<number>(Date.now());
  const lastAuthAt = useRef<number>(0);

  useEffect(() => {
    return onIdTokenChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
      if (u) setSignedOutReason(null);
    });
  }, []);

  // Idle timeout. R-1.5.
  useEffect(() => {
    if (!user) return;
    const bump = () => { lastActivity.current = Date.now(); };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const timer = window.setInterval(() => {
      if (Date.now() - lastActivity.current > IDLE_TIMEOUT_MS) {
        void doSignOut('Signed out after 12 hours of inactivity.');
      }
    }, 60_000);
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, bump));
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function doSignOut(reason?: string) {
    await fbSignOut(auth);
    lastAuthAt.current = 0;
    setSignedOutReason(reason ?? null);
  }

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      error,
      signedOutReason,
      signIn: async () => {
        setError(null);
        try {
          await signInWithPopup(auth, googleProvider);
          lastAuthAt.current = Date.now();
        } catch (e) {
          const code = (e as { code?: string }).code ?? '';
          // The blocking function rejects non-allowlisted accounts. Surface that
          // plainly rather than as a raw Firebase error string.
          if (
            code.includes('internal') ||
            code.includes('permission-denied') ||
            code.includes('blocking-function')
          ) {
            setError('This account is not authorized for BeckerSign.');
          } else if (code.includes('popup-closed') || code.includes('cancelled-popup')) {
            setError(null);
          } else if (code.includes('operation-not-allowed')) {
            setError('This sign-in method is disabled.');
          } else {
            setError('Sign-in failed. Please try again.');
          }
        }
      },
      signOut: doSignOut,
      isRecentlyAuthenticated: () => Date.now() - lastAuthAt.current < REAUTH_WINDOW_MS,
      reauthenticate: async () => {
        if (!auth.currentUser) return false;
        try {
          await reauthenticateWithPopup(auth.currentUser, googleProvider);
          lastAuthAt.current = Date.now();
          return true;
        } catch {
          return false;
        }
      },
    }),
    [user, loading, error, signedOutReason]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside AuthProvider');
  return v;
}
